//external libs
import React, { ReactElement, memo, useCallback, useMemo } from "react";
import classNames from "classnames";

// internal-deps
import { getCloudinaryUrl, Tick, useEffectOnce, isEmpty } from "@swiggy-private/core-ui";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { TICK_ACCENT } from "@swiggy-private/core-ui";

// components
import { PaymentMethodIcon } from "@payment-ui/widgets/payment-method-icon";

//types
import { PaymentMethodDef } from "@payment-ui/interfaces";

import styles from "./PaymentMethod.v4.scss";

interface PaymentMethodProps {
    id?: string;
    paymentMethodData: PaymentMethodDef;
    onSelectPaymentMethod: (method: PaymentMethodDef) => void;
    isSelected: boolean;
    onImpression: (methodData: PaymentMethodDef) => void;
}

const PaymentMethod = ({
    id,
    paymentMethodData,
    onSelectPaymentMethod,
    isSelected,
    onImpression,
}: PaymentMethodProps): ReactElement | null => {
    const onSelectPM = useCallback(() => {
        onSelectPaymentMethod(paymentMethodData);
    }, [paymentMethodData, onSelectPaymentMethod]);

    const titleClassName = useMemo(() => {
        return classNames(styles.paymentMethodTitle, {
            [styles.paymentMethodTitleSelected]: isSelected,
        });
    }, [isSelected]);

    const canShowBalance = useMemo(() => {
        if ((paymentMethodData.balanceData?.units ?? 0) > 1) {
            return true;
        }

        return false;
    }, [paymentMethodData]);

    useEffectOnce(() => {
        onImpression(paymentMethodData);
    });

    return (
        <div className={styles.paymentMethod} key={id} onClick={onSelectPM}>
            <PaymentMethodIcon
                src={getCloudinaryUrl(paymentMethodData?.meta?.iconUrl ?? "")}
                alt={paymentMethodData.displayName}
            />
            <div className={styles.paymentMethodContent}>
                <div className={titleClassName}>{paymentMethodData.displayName}</div>
                <div className={styles.paymentMethodSubtitle}>{paymentMethodData.inlineMessage}</div>
                {!isEmpty(paymentMethodData.discount) && (
                    <Text variant="V2.body3.neutral.medium" color="positive" className={styles.discountCallout}>
                        {paymentMethodData.discount}
                    </Text>
                )}
            </div>
            <div className={styles.paymentMethodBalanceContainer}>
                {canShowBalance && (
                    <div className={styles.paymentMethodBalance}>{paymentMethodData.balanceData?.units}</div>
                )}
                <div>
                    <Tick selected={isSelected} accent={TICK_ACCENT.ORANGE} />
                </div>
            </div>
        </div>
    );
};

export default memo(PaymentMethod);
